import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface AdminDashboardLayoutProps {
  children: React.ReactNode;
  adminName?: string;
}

const AdminDashboardLayout: React.FC<AdminDashboardLayoutProps> = ({
  children,
  adminName = 'Administrator'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768); // Open on desktop, closed on mobile
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false); // Desktop collapse state

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/admin/login');
    }
  };

  const navItems = [
    { key: 'overview', path: '/admin/dashboard', icon: '📊', label: 'Apžvalga' },
    { key: 'users', path: '/admin/dashboard/users', icon: '👥', label: 'Vartotojai' },
    { key: 'analytics', path: '/admin/dashboard/analytics', icon: '📈', label: 'Analitika' },
    { key: 'payments', path: '/admin/dashboard/payments', icon: '💳', label: 'Mokėjimai' },
    { key: 'recordings', path: '/admin/dashboard/recordings', icon: '📞', label: 'Įrašai' },
    { key: 'requests', path: '/admin/dashboard/requests', icon: '📋', label: 'Užklausos' },
    { key: 'help-chats', path: '/admin/dashboard/help-chats', icon: '💬', label: 'Pagalbos pokalbiai' },
    { key: 'test-voice', path: '/admin/dashboard/test-voice', icon: '🎙️', label: 'WebRTC testas' },
    { key: 'settings', path: '/admin/dashboard/settings', icon: '⚙️', label: 'Nustatymai' },
    { key: 'logs', path: '/admin/dashboard/logs', icon: '📝', label: 'Žurnalai' }
  ];

  const isActive = (path: string) => {
    if (path === '/admin/dashboard') {
      return location.pathname === '/admin/dashboard' || location.pathname === '/admin/dashboard/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Mobile Menu Button - Top Right, Sticky - Mobile Only */}
      {window.innerWidth < 768 && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1001,
            background: '#000042',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem',
            cursor: 'pointer',
            fontSize: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease'
          }}
        >
          {isSidebarOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Sidebar Overlay (for mobile) */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999,
            display: window.innerWidth < 768 ? 'block' : 'none'
          }}
        />
      )}

      {/* Sidebar */}
      <aside style={{
        width: window.innerWidth < 768 ? '100vw' : (isDesktopCollapsed ? '80px' : '260px'),
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e9ecef',
        padding: '2rem 0',
        position: 'fixed',
        height: '100vh',
        overflowY: 'auto',
        transition: 'all 0.3s ease',
        zIndex: 1000,
        transform: window.innerWidth >= 768 ? 'translateX(0)' : (isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)')
      }}>
        <div style={{
          padding: isDesktopCollapsed ? '0 1rem 2rem' : '0 2rem 2rem',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: '#000042',
          borderBottom: '1px solid #e9ecef',
          marginBottom: '2rem',
          fontFamily: 'Georgia, serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isDesktopCollapsed ? 'center' : 'space-between'
        }}>
          {!isDesktopCollapsed && <span>⚡ Valdymo panelė</span>}
          {window.innerWidth >= 768 && (
            <button
              onClick={() => setIsDesktopCollapsed(!isDesktopCollapsed)}
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                padding: '0.5rem',
                cursor: 'pointer',
                fontSize: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                marginLeft: isDesktopCollapsed ? '0' : 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e9ecef';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
              }}
              title={isDesktopCollapsed ? 'Išskleisti meniu' : 'Suskleisti meniu'}
            >
              {isDesktopCollapsed ? '☰' : '«'}
            </button>
          )}
        </div>

        <nav>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {navItems.map(item => (
              <li key={item.key} style={{ margin: '0.25rem 1rem' }}>
                <a
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                    if (window.innerWidth < 768) setIsSidebarOpen(false); // Close on mobile
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isDesktopCollapsed ? 'center' : 'flex-start',
                    padding: '0.875rem 1rem',
                    color: isActive(item.path) ? '#ffffff' : '#666',
                    backgroundColor: isActive(item.path) ? '#000042' : 'transparent',
                    textDecoration: 'none',
                    borderRadius: '25px',
                    transition: 'all 0.3s ease',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.color = '#000042';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#666';
                    }
                  }}
                  title={isDesktopCollapsed ? item.label : ''}
                >
                  <span style={{
                    marginRight: isDesktopCollapsed ? '0' : '0.75rem',
                    fontSize: '1.25rem'
                  }}>
                    {item.icon}
                  </span>
                  {!isDesktopCollapsed && item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout button at bottom */}
        <div style={{
          position: 'absolute',
          bottom: '2rem',
          left: '1rem',
          right: '1rem'
        }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              background: 'white',
              color: '#000042',
              border: '2px solid #e9ecef',
              borderRadius: '25px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontSize: '0.95rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#ef4444';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e9ecef';
              e.currentTarget.style.color = '#000042';
            }}
          >
            🚪 Atsijungti
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{
        flex: 1,
        marginLeft: window.innerWidth >= 768 ? (isDesktopCollapsed ? '80px' : '260px') : '0',
        padding: window.innerWidth >= 768 ? '2rem' : '1rem',
        paddingTop: window.innerWidth >= 768 ? '2rem' : '5rem',
        transition: 'all 0.3s ease'
      }}>
        {children}
      </main>
    </div>
  );
};

export default AdminDashboardLayout;
